/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { RadioButton, Note } from "@octopusdeploy/design-system-components";
import { ParseHelper } from "@octopusdeploy/design-system-octopus-components";
import type { DockerStopActionProperties } from "@octopusdeploy/legacy-action-properties";
import type { DeploymentActionResource, DeploymentStepResource } from "@octopusdeploy/octopus-server-client";
import { ActionExecutionLocation } from "@octopusdeploy/octopus-server-client";
import { flatten } from "lodash";
import { useCallback, useEffect, useState } from "react";
import type * as React from "react";
import { TargetRoles } from "~/areas/projects/components/Process/types";
import { BaseComponent } from "~/components/BaseComponent/BaseComponent";
import { StepMultiSelect } from "~/components/MultiSelect/StepMultiSelect";
import { BoundRadioButtonGroup } from "~/components/form/BoundField/BoundRadioButtonGroup";
import StringCheckbox from "~/components/form/BoundField/StringCheckbox";
import KeyValueItemList from "../../EditList/KeyValueEditList";
import ExpandableFormSection from "../../form/Sections/ExpandableFormSection";
import ExpanderSectionHeading from "../../form/Sections/FormSectionHeading";
import type { SummaryNode } from "../../form/Sections/Summary";
import Summary from "../../form/Sections/Summary";
import { VariableLookupText } from "../../form/VariableLookupText";
import Roles from "../Roles";
import type { ActionSummaryProps } from "../actionSummaryProps";
import type { ActionEditProps } from "../pluginRegistry";
import pluginRegistry from "../pluginRegistry";
class DockerStopActionSummary extends BaseComponent<ActionSummaryProps> {
    render() {
        return (<div>
                Stop a Docker Resource
                {this.props.targetRolesAsCSV && (<span>
                        {" "}
                        on deployment targets with <Roles rolesAsCSV={this.props.targetRolesAsCSV}/>
                    </span>)}
            </div>);
    }
    static displayName = "DockerStopActionSummary";
}
interface DockerStopActionState {
    name: string;
    steps: any;
}
const processStepsAndActions = (steps: DeploymentStepResource[]): (DeploymentActionResource & {
    NumberedName: string;
})[] => flatten(steps.map((step, stepIndex) => step.Actions.map((action, actionIndex) => {
    let num = `${stepIndex + 1}`;
    if (step.Actions.length > 1) {
        num = `${num}.${actionIndex + 1}`;
    }
    return {
        ...action,
        NumberedName: `${num}. ${action.Name}`,
    };
})));
const DockerStopAction: React.FC<ActionEditProps<DockerStopActionProperties>> = (props) => {
    const [name, setName] = useState<string>("XX");
    const [steps, setSteps] = useState<any[]>([]);
    const { doBusyTask, getProcessResource } = props;
    useEffect(() => {
        const fetchAndSetSteps = async () => {
            await doBusyTask(async () => {
                if (!getProcessResource) {
                    return;
                }
                const process = getProcessResource();
                const mainProcessActions = processStepsAndActions(process.Steps);
                // we can include the following if we want steps to be able to stop a container within a process template from an external step
                // const templateActions = flatten(stepTemplateCtx.map((contextItem) => processStepsAndActions(contextItem.processTemplate.Steps)));
                const allDockerActions = mainProcessActions.filter((action) => action.ActionType === "Octopus.DockerNetwork" || action.ActionType === "Octopus.DockerRun");
                setSteps(allDockerActions);
            });
        };
        fetchAndSetSteps();
    }, [doBusyTask, getProcessResource, setSteps]);
    const summaryTags = useCallback((): SummaryNode => {
        const tags = JSON.parse(props.properties["Octopus.Action.Docker.RemoveCustomTags"] || "{}");
        if (Object.keys(tags).length === 0) {
            return Summary.placeholder("No custom tags specified");
        }
        else {
            const text = Object.keys(tags)
                .map((m) => m + " = " + tags[m])
                .join(", ");
            return Summary.summary(text);
        }
    }, [props.properties]);
    const summarySteps = useCallback((): SummaryNode => {
        const removeStepIdsString = props.properties["Octopus.Action.Docker.RemoveSteps"];
        if (removeStepIdsString) {
            const removeStepsCount = removeStepIdsString.split(",").length;
            return Summary.summary(`${removeStepsCount} ${removeStepsCount === 1 ? "step has" : "steps have"} been selected`);
        }
        return Summary.placeholder("No steps have been selected");
    }, [props.properties]);
    const summaryMatches = useCallback((): SummaryNode => {
        const matches = [];
        if ((props.properties["Octopus.Action.Docker.RemoveByEnvironment"] || "false").toLowerCase() === "true") {
            matches.push("environment");
        }
        if ((props.properties["Octopus.Action.Docker.RemoveByRelease"] || "false").toLowerCase() === "true") {
            matches.push("release");
        }
        if ((props.properties["Octopus.Action.Docker.RemoveByTenant"] || "false").toLowerCase() === "true") {
            matches.push("tenant (if applicable)");
        }
        if (matches.length === 0) {
            return Summary.default("This project");
        }
        return Summary.summary(`The relevant ${matches.join(", ")} and this project`);
    }, [props.properties]);
    const propertySummary = useCallback((property: string, defaultValue: any): SummaryNode => {
        const subnet = (props.properties as any)[property];
        return subnet ? Summary.summary(subnet) : Summary.placeholder(defaultValue);
    }, [props.properties]);
    const summaryRemove = useCallback((): SummaryNode => {
        const remove = props.properties["Octopus.Action.Docker.Remove"];
        if (!remove || remove === "False") {
            return Summary.default("Stop");
        }
        else if (remove === "True") {
            return Summary.summary("Stop and Remove");
        }
        return Summary.summary(remove);
    }, [props.properties]);
    const selectedStopAndRemove = props.properties["Octopus.Action.Docker.Remove"] && props.properties["Octopus.Action.Docker.Remove"] === "True";
    return (<div>
            <ExpanderSectionHeading title="Rules"/>
            <ExpandableFormSection errorKey="Octopus.Action.Docker.Remove" isExpandedByDefault={props.expandedByDefault} title="Perform" summary={summaryRemove()} help="Action to take place.">
                <BoundRadioButtonGroup variableLookup={{
            localNames: props.localNames,
        }} resetValue={""} value={props.properties["Octopus.Action.Docker.Remove"] || "False"} onChange={(val) => props.setProperties({ ["Octopus.Action.Docker.Remove"]: val })}>
                    <RadioButton value={"False"} label={"Stop"} isDefault={true}/>
                    <RadioButton value={"True"} label={"Stop and remove"}/>
                </BoundRadioButtonGroup>
            </ExpandableFormSection>

            <ExpandableFormSection errorKey="Octopus.Action.Docker.RemoveSteps" isExpandedByDefault={props.expandedByDefault} title="From Steps" summary={summarySteps()} help={`Resources created from these Docker steps should be ${selectedStopAndRemove ? "stopped and removed" : "stopped"}.`}>
                <StepMultiSelect items={steps} label="Docker steps" value={ParseHelper.parseCSV(props.properties["Octopus.Action.Docker.RemoveSteps"])} onChange={(val) => props.setProperties({ ["Octopus.Action.Docker.RemoveSteps"]: ParseHelper.encodeCSV(val) })}/>
                <Note>
                    Note that leaving blank will {selectedStopAndRemove ? "stop and remove" : "stop"} resources created from <em>all</em> docker steps and therefore may {selectedStopAndRemove ? "stop and remove" : "stop"} resources from the same
                    release if this step runs after other Docker steps.
                </Note>
            </ExpandableFormSection>

            <ExpandableFormSection errorKey="Octopus.Action.Docker.RemoveByEnvironment|Octopus.Action.Docker.RemoveByRelease|Octopus.Action.Docker.RemoveByTenant" isExpandedByDefault={props.expandedByDefault} title="Resources Matching" summary={summaryMatches()} help="Containers created though Octopus are tagged with the relevant context to allow the following selection during this stop process.">
                <StringCheckbox value={"True"} onChange={(x) => undefined} disabled={true} label="Project" note={<span>Resources will be removed where they match the current project. This cannot be disabled.</span>}/>
                <br />
                <StringCheckbox value={props.properties["Octopus.Action.Docker.RemoveByEnvironment"]} onChange={(x) => props.setProperties({ ["Octopus.Action.Docker.RemoveByEnvironment"]: x })} label="Environment" note={<span>Resources will be removed only where they also match the current environment.</span>}/>
                <br />
                <StringCheckbox value={props.properties["Octopus.Action.Docker.RemoveByRelease"]} onChange={(x) => props.setProperties({ ["Octopus.Action.Docker.RemoveByRelease"]: x })} label="Release" note={<span>Resources will be removed only where they also match the current release.</span>}/>
                <br />
                <StringCheckbox value={props.properties["Octopus.Action.Docker.RemoveByTenant"]} onChange={(x) => props.setProperties({ ["Octopus.Action.Docker.RemoveByTenant"]: x })} label="Tenant (if applicable)" note={<span>If the release includes tenants then resources will be removed only where they also match the currently deploying tenant.</span>}/>
            </ExpandableFormSection>

            <ExpandableFormSection errorKey="Octopus.Action.Docker.RemoveCustomTags" isExpandedByDefault={props.expandedByDefault} title="Custom Tag Filters" summary={summaryTags()} help={<span>
                        These tags will be included when searching for resources using the <code>--filter "label=X"</code> argument. If the value is not provided, the resource will be included so long as the tag is present with any value
                    </span>}>
                <KeyValueItemList items={props.properties["Octopus.Action.Docker.RemoveCustomTags"]} separator="=" name="Custom tag" onChange={(val) => props.setProperties({ ["Octopus.Action.Docker.RemoveCustomTags"]: val })} keyLabel="Label" valueLabel="Value" localNames={props.localNames}/>
            </ExpandableFormSection>

            <ExpandableFormSection errorKey="Octopus.Action.Docker.StopTimeout" isExpandedByDefault={props.expandedByDefault} title="Timeout (sec)" summary={propertySummary("Octopus.Action.Docker.StopTimeout", "No timeout value supplied")} help={<span>
                        Seconds to wait for process to stop before killing it (Docker default 10) . The main process inside the container will receive <code>SIGTERM</code>, and after a grace period, <code>SIGKILL</code>.
                    </span>}>
                <VariableLookupText localNames={props.localNames} value={props.properties["Octopus.Action.Docker.StopTimeout"]} label="Timeout (sec)" placeholder="10" onChange={(val) => props.setProperties({ ["Octopus.Action.Docker.StopTimeout"]: val })}/>
            </ExpandableFormSection>
        </div>);
};
DockerStopAction.displayName = "DockerStopAction"
pluginRegistry.registerAction({
    executionLocation: ActionExecutionLocation.AlwaysOnTarget,
    actionType: "Octopus.DockerStop",
    summary: (properties, targetRolesAsCSV) => <DockerStopActionSummary properties={properties} targetRolesAsCSV={targetRolesAsCSV}/>,
    canHaveChildren: (step) => true,
    canBeChild: true,
    targetRoleOption: (action) => TargetRoles.Optional,
    editSections: { default: DockerStopAction },
    docsLink: "stopDockerResource",
});
